import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageHeader from "@/components/base/PageHeader";
import MatchCard from "@/components/base/MatchCard";
import { leagues } from "@/mocks/leagues";
import { matches } from "@/mocks/matches";
import { usePageMeta } from "@/hooks/usePageMeta";

const TODAY = "2026-10-15";
const YESTERDAY = "2026-10-14";

const filters = [
  { key: "today", label: "今日" },
  { key: "yesterday", label: "昨日" },
  { key: "upcoming", label: "未来 7 天" },
  { key: "all", label: "全部" },
] as const;

type FilterKey = (typeof filters)[number]["key"];

export default function Category() {
  const { league } = useParams<{ league: string }>();
  const current = leagues.find((l) => l.key === league);
  const [filter, setFilter] = useState<FilterKey>("all");
  const [visible, setVisible] = useState(6);

  usePageMeta(
    current
      ? `${current.name}足球赛事直播及回放 - 51网站在线观看免费播播足球`
      : "赛事分类 - 51网站在线观看免费播播足球",
    current
      ? `51足球直播为你汇总${current.name}（${current.en}）最新赛程、直播与回放，实时更新${current.region}${current.name}各场次比赛信息。`
      : "浏览各热门联赛的足球赛事直播与回放。",
  );

  if (!current) {
    return (
      <SiteLayout>
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-20 text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-primary-100 text-primary-600 flex items-center justify-center">
            <i className="ri-emotion-sad-line text-4xl" />
          </div>
          <h1 className="mt-6 text-2xl font-semibold text-foreground-950">
            没有找到该联赛
          </h1>
          <p className="mt-3 text-sm text-foreground-500">
            换个联赛看看，或返回首页浏览精选直播赛事。
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors cursor-pointer"
          >
            <i className="ri-home-4-line" />
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const leagueMatches = matches.filter((m) => m.leagueKey === current.key);
  const byTime = leagueMatches.filter((m) => {
    if (filter === "today") return m.date === TODAY;
    if (filter === "yesterday") return m.date === YESTERDAY;
    if (filter === "upcoming") return m.date > TODAY;
    return true;
  });
  const shown = byTime.slice(0, visible);

  return (
    <SiteLayout>
      <PageHeader
        title={`${current.name} 赛事直播及回放`}
        subtitle={`汇总 ${current.name}（${current.en}）最新赛程、实时直播与精彩回放，${current.region}赛事信息一站式查看。`}
        crumbs={[{ label: "首页", to: "/" }, { label: "赛事分类" }, { label: current.name }]}
        aside={
          <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-white/10 text-white text-sm backdrop-blur">
            <i className="ri-trophy-line text-accent-400" />
            共 {leagueMatches.length} 场赛事
          </div>
        }
      />

      <section className="w-full px-4 md:px-8 py-8 md:py-12 bg-background-50">
        <div className="w-full max-w-[1280px] mx-auto">
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <div className="inline-flex items-center gap-1 p-1 rounded-full bg-background-100">
              {filters.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => {
                    setFilter(f.key);
                    setVisible(6);
                  }}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    filter === f.key
                      ? "bg-primary-500 text-white"
                      : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {shown.length === 0 ? (
            <div className="py-16 text-center bg-background-100 rounded-lg border border-background-200">
              <i className="ri-calendar-close-line text-4xl text-foreground-300" />
              <p className="mt-3 text-sm text-foreground-500">
                该时间段暂无 {current.name} 比赛
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() => setFilter("all")}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors cursor-pointer"
                >
                  <i className="ri-refresh-line" />
                  查看全部比赛
                </button>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-50 border border-background-200 hover:bg-background-100 text-foreground-800 text-sm font-medium transition-colors cursor-pointer"
                >
                  返回首页
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
                {shown.map((m) => (
                  <MatchCard key={m.id} match={m} />
                ))}
              </div>
              {visible < byTime.length && (
                <div className="mt-8 text-center">
                  <button
                    type="button"
                    onClick={() => setVisible((v) => v + 6)}
                    className="inline-flex items-center gap-2 px-7 py-3 rounded-md bg-background-50 border border-background-200 hover:bg-primary-50 hover:border-primary-300 text-foreground-800 text-sm font-medium transition-colors cursor-pointer"
                  >
                    加载更多比赛
                    <i className="ri-arrow-down-line" />
                  </button>
                </div>
              )}
            </>
          )}

          <div className="mt-12 pt-8 border-t border-background-200">
            <h2 className="text-base font-semibold text-foreground-950 mb-4">
              切换其他联赛
            </h2>
            <div className="flex flex-wrap gap-2">
              {leagues.map((lg) => (
                <Link
                  key={lg.key}
                  to={`/category/${lg.key}`}
                  className={`px-4 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    lg.key === current.key
                      ? "bg-primary-500 text-white"
                      : "bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-700"
                  }`}
                >
                  {lg.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}